import Typography from '@mui/material/Typography';
import Button, { ButtonSize } from '@packages/shared-ui/v4/button';
import Paper from '@packages/shared-ui/v4/paper';
import RichText from '@packages/shared-ui/v4/rich-text';
import { useId } from 'react';
import ResponsiveImage from '@/components/Image/ResponsiveImage';
import type { SubscriptionOfferCF } from '@/types';
import useStyles from './SubscriptionProductCard.styles';
import SunrunMembership from './SunrunMembership';
import {
  getOfferMembership,
  getOfferProducts,
  getRasterImage,
  isSubscriptionBundle,
} from './subscriptionOffer';

const FEATURED_BORDER_WIDTH_PX = 3;
const PRICE_PLACEHOLDER = '$X.xx/yr';

interface Props {
  offer: SubscriptionOfferCF;
  onSelect: () => void;
}

export default function SubscriptionProductCard({ offer, onSelect }: Props) {
  const {
    fields: { name, description, details, ctaLabel, isFeatured, image },
  } = offer;
  const { classes, cx } = useStyles();
  const titleId = useId();
  const frontifyImage = getRasterImage(image);
  const membership = getOfferMembership(offer);
  const isBundle = isSubscriptionBundle(offer);

  return (
    <Paper
      component='article'
      aria-labelledby={titleId}
      glow={isFeatured}
      borderWidth={isFeatured ? FEATURED_BORDER_WIDTH_PX : undefined}
      className={cx(
        classes.root,
        isFeatured ? classes.featured : classes.outlined
      )}
    >
      {isFeatured && (
        <Typography
          component='span'
          variant='captionSemibold'
          className={classes.pill}
        >
          Best Value
        </Typography>
      )}
      <div className={classes.imageContainer}>
        {frontifyImage && (
          <ResponsiveImage
            src={frontifyImage}
            alt={frontifyImage.title ?? ''}
            sizes={{ xs: 640, md: 480 }}
            className={classes.image}
          />
        )}
      </div>
      <div className={classes.header}>
        <div className={classes.titleBlock}>
          <div className={classes.titleRow}>
            <Typography variant='h3' component='h2' id={titleId}>
              {name}
            </Typography>
            {!isBundle && (
              <Typography variant='h3' component='p'>
                {PRICE_PLACEHOLDER}
              </Typography>
            )}
          </div>
          {description && (
            <Typography variant='bodyMd' color='semantic.text.secondary'>
              {description}
            </Typography>
          )}
        </div>
        {isBundle && (
          <ul
            className={classes.bundleProducts}
            aria-label={`${name} includes`}
          >
            {getOfferProducts(offer).map((product) => (
              <li key={product.sys.id} className={classes.titleRow}>
                <Typography variant='h3' component='span'>
                  {product.fields.name}
                </Typography>
                <Typography variant='h3' component='span'>
                  {PRICE_PLACEHOLDER}
                </Typography>
              </li>
            ))}
          </ul>
        )}
      </div>
      {membership && <SunrunMembership membership={membership} />}
      <Button
        size={{ xs: ButtonSize.SM, md: ButtonSize.MD }}
        fullWidth
        className={classes.cta}
        onClick={onSelect}
      >
        {ctaLabel}
      </Button>
      {details && (
        <RichText
          content={details}
          className={classes.details}
          variants={{ p: 'bodyMd' }}
          blockProps={{ p: { color: 'semantic.text.secondary' } }}
        />
      )}
    </Paper>
  );
}
